import { Card } from '@wundo/ui';
import { debriefTookPart } from '@wundo/shared/debriefTookPart.ts';
import type { RoleplayDebrief } from '@wundo/shared/roleplay.ts';
import { PracticeSessionFeedback } from './PracticeSessionFeedback.tsx';
import { practiceFeedbackTitle } from './practiceFeedbackTitle.ts';
import { m } from '@/paraglide/messages.js';

export interface PracticeSessionEndProps {
  /** Null when nobody spoke: there is nothing to give feedback on. */
  debrief: RoleplayDebrief | null;
}

/**
 * How a practice conversation ends at its own address.
 *
 * The same report the dialog on Oefenen shows, in the only frame this screen
 * can give it: a card at the width every report in the app is read at, under
 * the logo. A page heading over a full-window surface was the old idiom and it
 * read as a screen that had not finished loading — the conversation it belonged
 * to is over, and what is left is a document.
 *
 * Everyone who practises at this address reads their feedback here, whether
 * they signed in or came in on a link: this conversation is the whole of what
 * they opened, and Oefenen is a page most of them never asked for — so nothing
 * here points at it either.
 *
 * Two endings, and the heading is the difference. One has feedback; the other
 * has none, and says so rather than promising a report that will never be
 * written.
 */
export function PracticeSessionEnd({ debrief }: PracticeSessionEndProps) {
  const empty = debrief === null || !debriefTookPart(debrief);
  const title = practiceFeedbackTitle(debrief);

  return (
    <Card
      as='section'
      aria-label={title}
      className='mx-auto flex w-full max-w-190 flex-col gap-4'
    >
      <div>
        <h2 className='text-xl font-medium tracking-tight text-ink'>{title}</h2>
        {!empty && (
          <p className='mt-1.5 text-md text-ink-muted'>
            {m.practice_session_done_body()}
          </p>
        )}
      </div>

      <PracticeSessionFeedback debrief={debrief} />
    </Card>
  );
}
